import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, warrantyPillClass, warrantyLabel, type Asset } from "../lib/api.ts";

type AssetRow = Asset & { orgName: string | null; qaPassed: boolean; warranty: string };

// Staff asset / warranty console (COMMISSIONING_WARRANTY.md P5.2): the full asset
// inventory (every commissioned unit, newest first) plus a direct serial lookup.
export function StaffAssetsPage() {
  const nav = useNavigate();
  const [serial, setSerial] = useState("");

  const inventory = useQuery({
    queryKey: ["staff", "assets"],
    queryFn: () => api.staffAssetsList(),
  });

  return (
    <>
      <h1>Assets</h1>
      <p className="muted">
        Every commissioned unit, from factory stock to the field. Open one to see its
        warranty, current hardware, event history and QA/admin actions.
      </p>
      <div className="card narrow">
        <form
          className="formstack"
          onSubmit={(e) => {
            e.preventDefault();
            const s = serial.trim();
            if (s) nav(`/staff/assets/${encodeURIComponent(s)}`);
          }}
        >
          <label>Asset serial
            <input
              type="text"
              value={serial}
              placeholder="BT1851-XXXX-XXXX"
              onChange={(e) => setSerial(e.target.value)}
            />
          </label>
          <div className="row"><button type="submit">Look up</button></div>
        </form>
      </div>

      {inventory.isLoading && <p className="muted">Loading inventory…</p>}
      {inventory.error && (
        <p className="err">Couldn't load inventory — {(inventory.error as Error).message}</p>
      )}
      {inventory.data && inventory.data.assets.length === 0 && (
        <div className="card"><p className="muted">No assets commissioned yet.</p></div>
      )}
      {inventory.data && inventory.data.assets.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Asset serial</th><th>CM5 serial</th><th>Status</th><th>Holder</th>
                <th>QA</th><th>Warranty</th><th>Commissioned</th>
              </tr>
            </thead>
            <tbody>
              {inventory.data.assets.map((a: AssetRow) => (
                <tr key={a.assetSerial}>
                  <td>
                    <Link to={`/staff/assets/${encodeURIComponent(a.assetSerial)}`}>
                      <code className="code-sm">{a.assetSerial}</code>
                    </Link>
                  </td>
                  <td><code className="code-xs muted">{a.serial ?? "—"}</code></td>
                  <td>{a.status}</td>
                  <td className="muted">{a.orgName ?? a.orgId}</td>
                  <td>
                    {a.qaPassed
                      ? <span className="pill pass">passed</span>
                      : <span className="pill cancelled">pending</span>}
                  </td>
                  <td><span className={warrantyPillClass(a.warranty)}>{warrantyLabel(a.warranty)}</span></td>
                  <td className="muted">
                    {a.commissionedAt ? new Date(a.commissionedAt).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
